import { CircleAlertIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import calendarIcon from '@/assets/icons/calendar.svg';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { autoFormatDate, formatDate, getEndDateError, getStartDateError } from '@/utils/dateUtils';
import { cn } from '@/utils/utils';

interface PreviousRequest {
  count: number;
  countLabel: string;
  fromLabel: string;
  id: string;
  isFailed: boolean;
  isLoading: boolean;
  jobId: string | null;
  subtitle: string;
  toLabel: string;
}

interface CustomRequestCardProps {
  className?: string;
  endDate?: string;
  onEndDateChange?: (value: string) => void;
  onGenerateClick?: () => void | Promise<void>;
  onStartDateChange?: (value: string) => void;
  startDate?: string;
}

interface PreviousRequestsListProps {
  className?: string;
  onRequestClick?: (request: PreviousRequest) => void;
  requests: Array<PreviousRequest>;
}

export function CustomNarrativeRequestCard({
  className,
  endDate,
  onEndDateChange,
  onGenerateClick,
  onStartDateChange,
  startDate,
}: CustomRequestCardProps) {
  const { t } = useTranslation();
  // Check if both dates are completely filled (10 characters = DD/MM/YYYY)
  const bothDatesFilled = Boolean(
    startDate && endDate && startDate.length === 10 && endDate.length === 10,
  );

  // Validate dates only if they are complete (10 characters)
  const startDateError = startDate && startDate.length === 10 ? getStartDateError(startDate) : null;
  const endDateError =
    endDate && endDate.length === 10 ? getEndDateError(startDate ?? '', endDate) : null;
  const hasErrors = Boolean(startDateError || endDateError);

  const isButtonDisabled = !bothDatesFilled || hasErrors;

  return (
    <div
      className={cn(
        // Request card (Custom date range)
        // - Wire the "Generate Narratives" button to call the backend with the
        //   selected date range and refresh the PreviousRequestsList via query.
        // Layout: full-width, radius 12, padding 12, subtle shadow
        'flex min-h-[84px] w-full flex-col gap-3 overflow-visible rounded-[12px] bg-card px-3 py-3 shadow-[0_1px_5px_0_#71778B1A]',
        className,
      )}
    >
      <p className="text-secondary font-sans text-[13px] leading-[170%] font-medium">
        {t('narrative.request.byDate')}
      </p>

      <div className="flex flex-col gap-2">
        <div className="flex w-full items-start gap-2">
          <div className="min-w-[100px] flex-1">
            <InlineDatePicker
              onChange={onStartDateChange}
              otherDate={endDate}
              type="start"
              value={startDate}
            />
          </div>
          <div className="flex h-7 shrink-0 items-center">
            <span className="text-secondary px-1 font-sans text-sm">
              {t('narrative.dateRange.to')}
            </span>
          </div>
          <div className="min-w-[100px] flex-1">
            <InlineDatePicker
              onChange={onEndDateChange}
              otherDate={startDate}
              type="end"
              value={endDate}
            />
          </div>
        </div>
      </div>

      <div
        className="relative z-50 pt-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          className={cn(
            'relative z-50 h-8 w-full rounded-[8px] font-sans text-[13px] font-medium text-primary-foreground shadow-none transition-colors hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50',
            !isButtonDisabled
              ? 'cursor-pointer bg-primary hover:bg-primary-hover active:bg-primary-pressed'
              : 'bg-primary-disabled hover:bg-primary-disabled',
          )}
          disabled={isButtonDisabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isButtonDisabled && onGenerateClick) {
              try {
                const result = onGenerateClick();
                result?.catch((error: Error) => {
                  console.error('Error in onGenerateClick:', error);
                });
              } catch (error) {
                console.error('Error calling onGenerateClick:', error);
              }
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          style={{ pointerEvents: 'auto', position: 'relative', zIndex: 9999 }}
          type="button"
        >
          {t('narrative.request.generate')}
        </button>
      </div>
    </div>
  );
}

// Helper to parse DD/MM/YYYY into Date
function parseDate(value?: string): Date | undefined {
  if (!value) {
    return undefined;
  }
  const [day, month, year] = value.split('/');
  if (!day || !month || !year) {
    return undefined;
  }
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

// Helper to get date-only (without time) for comparison
function getDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Helper to validate if date is within valid range
function isDateValid(date: Date): { isValid: boolean; reason?: 'future' } {
  const dateOnly = getDateOnly(date);
  const todayOnly = getDateOnly(new Date());

  // Check if date is after today
  if (dateOnly > todayOnly) {
    return { isValid: false, reason: 'future' };
  }

  return { isValid: true };
}

function InlineDatePicker({
  onChange,
  otherDate,
  type,
  value,
}: {
  onChange?: ((value: string) => void) | undefined;
  otherDate?: string | undefined;
  type: 'start' | 'end';
  value?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const isFilled = Boolean(value);
  const today = useMemo(() => getDateOnly(new Date()), []);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use validation functions from dateUtils (same as popup version)
  const error = useMemo(() => {
    if (type === 'start') {
      return getStartDateError(value ?? '');
    } else {
      return getEndDateError(otherDate ?? '', value ?? '');
    }
  }, [type, value, otherDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Auto-format with slashes as user types
    const formatted = autoFormatDate(inputValue, value ?? '');
    onChange?.(formatted);
  };

  const handleInputBlur = () => {
    // Validate format on blur
    if (value?.length === 10) {
      const [day, month, year] = value.split('/');
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      if (!Number.isNaN(parsed.getTime())) {
        const validation = isDateValid(parsed);
        if (!validation.isValid) {
          // Clear invalid (future) date
          onChange?.('');
          return;
        }
        // Valid date, keep it
        return;
      }
    }
    // If incomplete or invalid, keep what user typed for them to correct
  };

  return (
    <div className="flex flex-col">
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverAnchor asChild>
          <div className="relative flex w-full" ref={anchorRef}>
            <div
              className={cn(
                'flex h-7 w-full items-center gap-2 overflow-hidden rounded-[4px] border px-2 font-sans text-[12px] font-normal transition-colors',
                error
                  ? 'border-[0.5px] border-destructive-border bg-destructive-bg'
                  : 'border-transparent hover:border-border-strong hover:bg-elevated-subtle',
                !error && isFilled ? 'bg-back-bg' : !error ? 'bg-back-bg2' : '',
              )}
            >
              <button
                className="relative z-10 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  inputRef.current?.focus();
                  setOpen(true);
                }}
                type="button"
              >
                <img
                  alt=""
                  className="icon-themed text-neutral-mid pointer-events-none h-3 w-3"
                  src={calendarIcon}
                />
              </button>
              <input
                className={cn(
                  'relative z-10 min-w-[80px] flex-1 cursor-text border-0 bg-transparent p-0 font-sans text-[12px] leading-[170%] font-normal tracking-wide outline-none',
                  error
                    ? 'text-foreground placeholder:text-destructive-border'
                    : isFilled
                      ? 'text-foreground placeholder:text-neutral-mid'
                      : 'text-neutral-mid placeholder:text-neutral-mid',
                )}
                maxLength={10}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
                onFocus={() => setOpen(true)}
                placeholder="DD/MM/YYYY"
                ref={inputRef}
                type="text"
                value={value ?? ''}
              />
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          alignOffset={-12}
          className="border-border-subtle bg-popover z-50 w-[min(270px,calc(100vw-24px))] overflow-hidden rounded-lg border p-2 shadow-lg"
          collisionPadding={12}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            const target = e.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              e.preventDefault();
            }
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            queueMicrotask(() => inputRef.current?.focus({ preventScroll: true }));
          }}
          side="bottom"
          sideOffset={8}
        >
          <Calendar
            captionLayout="dropdown"
            className="w-full"
            defaultMonth={parseDate(value) ?? today}
            disabled={(date) => !isDateValid(date).isValid}
            endMonth={new Date(today.getFullYear(), today.getMonth(), 1)}
            fixedWeeks={false}
            mode="single"
            onSelect={(date) => {
              const formatted = date ? formatDate(date) : '';
              onChange?.(formatted);
              setOpen(false);
            }}
            selected={parseDate(value)}
            showOutsideDays={false}
            toDate={today}
          />
        </PopoverContent>
      </Popover>
      {error && (
        <div className="mt-0.5 flex h-[14px] items-center">
          <div className="flex items-center gap-1 px-1">
            <CircleAlertIcon className="text-destructive size-2.5 shrink-0" />
            <p className="text-destructive text-[10px] leading-[160%] font-medium">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function PreviousRequestsList({
  className,
  onRequestClick,
  requests,
}: PreviousRequestsListProps) {
  const { t } = useTranslation();

  if (!requests.length) {
    return null;
  }

  return (
    <div
      className={cn(
        // Wrapper for previous requests section – full width to align with top card
        'flex flex-col gap-2 overflow-hidden rounded-[12px] bg-back-bg2 pt-3',
        className,
      )}
    >
      <p className="text-foreground mb-1 font-sans text-sm font-medium">
        {t('narrative.previousRequests.title')}
      </p>
      {requests.map((request) => (
        <PreviousRequestCard
          key={request.id}
          request={request}
          {...(onRequestClick !== undefined && { onClick: () => onRequestClick(request) })}
        />
      ))}
    </div>
  );
}

export function PreviousRequestsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="bg-back-bg2 flex flex-col gap-2 overflow-hidden rounded-[12px] px-3 pt-3 pb-2">
      <p className="text-foreground mb-1 font-sans text-sm font-medium">Previous Requests</p>
      {Array.from({ length: count }).map((_, index) => (
        <div
          className="bg-card flex w-full flex-col gap-2 rounded-[8px] px-3 py-2 shadow-[0_1px_5px_0_#71778B1A]"
          key={index}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <Skeleton className="h-6 w-[88px] rounded-[4px]" />
              <Skeleton className="h-3 w-4" />
              <Skeleton className="h-6 w-[88px] rounded-[4px]" />
            </div>
            <div className="flex min-w-[52px] flex-col items-end gap-1">
              <Skeleton className="h-3 w-5" />
              <Skeleton className="h-2 w-14" />
            </div>
          </div>
          <Skeleton className="h-3 w-[60%]" />
        </div>
      ))}
    </div>
  );
}

function PreviousRequestCard({
  onClick,
  request,
}: {
  onClick?: () => void;
  request: PreviousRequest;
}) {
  const { t } = useTranslation();
  return (
    <Button
      className="group hover:bg-back-bg2 active:bg-elevated-subtle bg-card flex min-h-[68px] w-full cursor-pointer items-center justify-between gap-2 rounded-[8px] px-3 py-2 text-left shadow-[0_1px_5px_0_#71778B1A] transition-all active:shadow-[0_1px_4px_0_#71778B40]"
      onClick={onClick}
      type="button"
      variant="ghost"
    >
      {/* Left side: date range + timestamp stacked */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className="bg-back-bg2 text-foreground group-hover:bg-elevated-subtle inline-flex h-6 w-[88px] items-center justify-center rounded-[4px] px-2 font-sans text-[12px] leading-[170%] font-medium whitespace-nowrap transition-colors">
            {request.fromLabel}
          </span>
          <span className="text-secondary font-sans text-sm">to</span>
          <span className="bg-back-bg2 text-foreground group-hover:bg-elevated-subtle inline-flex h-6 w-[88px] items-center justify-center rounded-[4px] px-2 font-sans text-[12px] leading-[170%] font-medium whitespace-nowrap transition-colors">
            {request.toLabel}
          </span>
        </div>
        {request.isLoading ? (
          <Skeleton className="h-3 w-[120px]" />
        ) : (
          <p className="text-secondary font-sans text-[10px] leading-[160%]">{request.subtitle}</p>
        )}
      </div>

      {/* Right side: count vertically centered */}
      <div className="flex h-[44px] min-w-[52px] shrink-0 flex-col items-end justify-center pl-1">
        {request.isLoading ? (
          <>
            <Skeleton className="mb-1 h-3 w-5" />
            <Skeleton className="h-2 w-14" />
          </>
        ) : request.isFailed ? (
          <div className="flex flex-col items-end gap-0.5">
            <CircleAlertIcon className="text-destructive size-4" />
            <p className="text-destructive text-right font-sans text-[11px] leading-[160%] font-medium">
              {t('narrative.previousRequests.failed')}
            </p>
          </div>
        ) : (
          <>
            <p className="text-foreground text-right font-sans text-[13px] leading-[170%] font-semibold">
              {request.countLabel}
            </p>
            <p className="text-secondary mt-0.5 text-right font-sans text-[11px] leading-[160%]">
              {t('narrative.header.title')}
            </p>
          </>
        )}
      </div>
    </Button>
  );
}
